import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import PasswordStrengthMeter from '@/components/auth/PasswordStrengthMeter';
import { haptics } from '@/utils/haptics';

/**
 * RegisterPage - Modern Mobile App Sign-Up Screen
 * 
 * Matches LoginPage design with:
 * - Soft neutral background
 * - Large centered logo with animation
 * - Clean white card for form
 * - Polished iPhone app feel
 */

const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordValid, setPasswordValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { register, isAuthenticated } = useAuth();

  // If already authenticated, redirect
  if (isAuthenticated) {
    navigate('/', { replace: true });
    return null;
  }

  const handleRegister = async (e) => {
    e.preventDefault();
    haptics.press();
    
    if (!passwordValid) {
      haptics.warning();
      toast.error('Password does not meet requirements', {
        description: 'Please check the password requirements below'
      });
      return;
    }
    
    setLoading(true);

    try {
      await register(name, email, password);
      haptics.success();
      toast.success('Account created!', {
        description: "Please check your email to verify your account"
      });
      navigate('/', { replace: true });
    } catch (error) {
      haptics.error();
      const status = error.response?.status;
      const detail = error.response?.data?.detail?.toLowerCase() || '';
      const errorCode = error.code;
      
      if (status === 409 || detail.includes('already') || detail.includes('exists') || detail.includes('registered')) {
        toast.error('Email already registered', {
          description: 'Try signing in instead'
        });
      } else if (detail.includes('password') && detail.includes('short')) {
        toast.error('Password too short', {
          description: 'Please use at least 8 characters'
        });
      } else if (detail.includes('email') && detail.includes('invalid')) {
        toast.error('Invalid email address', {
          description: 'Please check and try again'
        });
      } else if (!navigator.onLine) {
        toast.error('No internet connection', {
          description: 'Please check your connection and try again'
        });
      } else if (errorCode === 'ERR_NETWORK' || errorCode === 'ERR_CONNECTION_REFUSED' || error.message?.includes('Network')) {
        toast.error('Cannot connect to server', {
          description: 'Please check your internet connection or try again later'
        });
      } else {
        // Show actual error for debugging
        const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
        console.error('Registration error:', error);
        toast.error('Something went wrong', {
          description: String(errorMsg).substring(0, 100)
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F7F5] flex flex-col">
      {/* Logo Section */}
      <div className="flex flex-col items-center justify-center px-6 pt-12 pb-6">
        {/* Animated Logo */}
        <div className="animate-logo-enter mb-3">
          <svg 
            width="64" 
            height="64" 
            viewBox="0 0 100 100" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            className="drop-shadow-sm"
          >
            {/* Flower petals - soft blue forget-me-not */}
            <circle cx="50" cy="30" r="14" fill="#7BB8E0" />
            <circle cx="70" cy="42" r="14" fill="#8DC4E8" />
            <circle cx="65" cy="65" r="14" fill="#7BB8E0" />
            <circle cx="35" cy="65" r="14" fill="#8DC4E8" />
            <circle cx="30" cy="42" r="14" fill="#7BB8E0" />
            {/* Center */}
            <circle cx="50" cy="50" r="10" fill="#FFE082" />
            <circle cx="50" cy="50" r="5" fill="#FFC107" />
          </svg>
        </div>
        
        {/* App Name */}
        <h1 className="text-xl font-semibold text-slate-800 mb-1 animate-logo-enter animation-delay-100">
          Forget Me Not
        </h1>
        
        {/* Welcome Text */}
        <p className="text-slate-500 animate-logo-enter animation-delay-200">
          Create your account
        </p>
      </div>

      {/* Form Card */}
      <div className="flex-1 px-5 pb-8">
        <div 
          className="bg-white rounded-2xl p-6 space-y-4 animate-card-enter"
          style={{ 
            boxShadow: '0 2px 16px -4px rgba(0, 0, 0, 0.08), 0 0 1px rgba(0, 0, 0, 0.04)'
          }}
        >
          <form onSubmit={handleRegister} className="space-y-4">
            {/* Name Field */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium text-slate-700">
                Full Name
              </Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="h-12 text-base px-4 rounded-xl border-slate-200 focus:border-blue-400 focus:ring-blue-400 bg-slate-50/50"
                data-testid="register-name-input"
              />
            </div>

            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 text-base px-4 rounded-xl border-slate-200 focus:border-blue-400 focus:ring-blue-400 bg-slate-50/50"
                data-testid="register-email-input"
              />
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a strong password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 text-base px-4 pr-12 rounded-xl border-slate-200 focus:border-blue-400 focus:ring-blue-400 bg-slate-50/50"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              <PasswordStrengthMeter 
                password={password} 
                onValidChange={setPasswordValid}
              />
            </div>

            {/* Create Account Button */}
            <Button
              type="submit"
              className="w-full h-13 text-base font-semibold rounded-xl bg-[#5B9BD5] hover:bg-[#4A8BC5] transition-colors mt-2"
              style={{ height: '52px' }}
              disabled={loading || !passwordValid}
              data-testid="register-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>

            {/* Terms */}
            <p className="text-xs text-center text-slate-500 pt-1">
              By creating an account, you agree to our{' '}
              <Link to="/terms" className="text-blue-500 hover:underline">Terms</Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-blue-500 hover:underline">Privacy Policy</Link>
            </p>
          </form>
        </div>

        {/* Sign In Link */}
        <div className="text-center mt-6">
          <p className="text-slate-500">
            Already have an account?{' '}
            <Link 
              to="/login" 
              className="text-blue-500 hover:text-blue-600 font-semibold"
              data-testid="signin-link"
            >
              Sign In
            </Link>
          </p>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes logoEnter {
          0% {
            opacity: 0;
            transform: scale(0.9);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes cardEnter {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-logo-enter {
          animation: logoEnter 0.5s ease-out forwards;
        }
        
        .animate-card-enter {
          animation: cardEnter 0.4s ease-out 0.2s forwards;
          opacity: 0;
        }
        
        .animation-delay-100 {
          animation-delay: 0.1s;
          opacity: 0;
        }
        
        .animation-delay-200 {
          animation-delay: 0.2s;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

export default RegisterPage;
